import React, { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { QRCodeCanvas } from "qrcode.react";
import {
  Copy,
  Download,
  ExternalLink,
  RefreshCw,
  Share2,
} from "lucide-react";

const WEBMENU_BASE_URL = "https://webmenu.megriva.com";

export default function QRCodePage() {
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadBusiness = async () => {
    try {
      setLoading(true);

      const rawUser = localStorage.getItem("user");

      if (!rawUser) {
        toast.error("Kullanıcı bilgisi bulunamadı. Lütfen tekrar giriş yapın.");
        setBusiness(null);
        return;
      }

      let user;

      try {
        user = JSON.parse(rawUser);
      } catch (e) {
        console.error("user parse hatası:", e);
        toast.error("Kullanıcı bilgisi okunamadı.");
        setBusiness(null);
        return;
      }

      const businessId =
        user.businessId ?? user.business_id ?? user.BusinessId;

      if (!businessId) {
        toast.error("Restoran bilgisi bulunamadı.");
        setBusiness(null);
        return;
      }

      const res = await api.getBusinessById(businessId);
      const businessData = res?.data ?? null;

      setBusiness(businessData);
    } catch (error) {
      console.error("Restoran bilgisi alınırken hata:", error);
      toast.error("Restoran bilgileri alınırken bir hata oluştu.");
      setBusiness(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBusiness();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const businessSlug = useMemo(() => {
    return (
      business?.subDomain ||
      business?.SubDomain ||
      business?.businessSubDomain ||
      business?.business_sub_domain ||
      business?.slug ||
      ""
    )
      .toString()
      .trim();
  }, [business]);

  const menuUrl = useMemo(() => {
    if (!businessSlug) return "";
    return `${WEBMENU_BASE_URL}/${encodeURIComponent(businessSlug)}`;
  }, [businessSlug]);

  const downloadCanvas = (canvasId) => {
    if (!menuUrl) {
      toast.error("QR linki oluşturulamadı.");
      return;
    }

    const canvas = document.getElementById(canvasId);

    if (!canvas) {
      toast.error("QR kod hazırlanamadı.");
      return;
    }

    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");

    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `qr-menu-${
      businessSlug || business?.id || "business"
    }.png`;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleDownloadQR = () => {
    downloadCanvas("business-qr");
  };

  const handleDownloadMobileQR = () => {
    downloadCanvas("business-qr-mobile");
  };

  const handleOpenMenu = () => {
    if (!menuUrl) {
      toast.error("Menü bağlantısı oluşturulamadı.");
      return;
    }

    window.open(menuUrl, "_blank", "noopener,noreferrer");
  };

  const copyMenuUrl = async () => {
    if (!menuUrl) {
      toast.error("Menü bağlantısı oluşturulamadı.");
      return false;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(menuUrl);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = menuUrl;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";

        document.body.appendChild(textarea);
        textarea.select();

        const copied = document.execCommand("copy");
        document.body.removeChild(textarea);

        if (!copied) {
          throw new Error("Kopyalama desteklenmiyor.");
        }
      }

      toast.success("Menü bağlantısı kopyalandı.");
      return true;
    } catch (error) {
      console.error("MENU URL COPY ERROR:", error);
      toast.error("Menü bağlantısı kopyalanamadı.");
      return false;
    }
  };

  const handleShareMenu = async () => {
    if (!menuUrl) {
      toast.error("Menü bağlantısı oluşturulamadı.");
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${business?.name || "İşletme"} QR Menü`,
          text: `${business?.name || "İşletme"} menüsü`,
          url: menuUrl,
        });
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;

        console.error("MENU SHARE ERROR:", error);
      }
    }

    await copyMenuUrl();
  };

  const handleShareQRImage = async () => {
    if (!menuUrl) {
      toast.error("QR linki oluşturulamadı.");
      return;
    }

    const canvas = document.getElementById("business-qr-mobile");

    if (!canvas) {
      toast.error("QR kod hazırlanamadı.");
      return;
    }

    try {
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((result) => {
          if (result) {
            resolve(result);
            return;
          }

          reject(new Error("QR görseli oluşturulamadı."));
        }, "image/png");
      });

      const file = new File(
        [blob],
        `qr-menu-${businessSlug || business?.id || "business"}.png`,
        { type: "image/png" }
      );

      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          title: `${business?.name || "İşletme"} QR Menü`,
          text: `${business?.name || "İşletme"} menüsü`,
          files: [file],
        });
        return;
      }

      handleDownloadMobileQR();
      toast.success("QR kod indirildi.");
    } catch (error) {
      if (error?.name === "AbortError") return;

      console.error("QR IMAGE SHARE ERROR:", error);
      handleDownloadMobileQR();
    }
  };

  return (
    <AdminLayout>
      {/* =========================================================
          MOBILE WEB
          Browser-native actions + app-consistent information hierarchy.
         ========================================================= */}
      <div className="min-h-full bg-[#F8F8F8] px-5 pb-10 pt-4 md:hidden">
        {loading ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#E4E4E7] border-t-[#EA580C]" />
            <p className="mt-3 text-sm text-[#71717A]">QR kod hazırlanıyor...</p>
          </div>
        ) : !business ? (
          <div className="rounded-[20px] border border-red-200 bg-red-50 p-5">
            <p className="text-sm font-bold text-red-800">
              İşletme bilgileri alınamadı.
            </p>

            <button
              type="button"
              onClick={loadBusiness}
              className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl border border-red-200 bg-white px-4 text-sm font-semibold text-red-700 active:bg-red-50"
            >
              <RefreshCw className="h-4 w-4" />
              Tekrar Dene
            </button>
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-center gap-3 px-0.5">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-extrabold tracking-[0.08em] text-[#EA580C]">
                  QR MENÜ
                </p>

                <h1 className="mt-0.5 truncate text-[22px] font-extrabold leading-7 text-[#18181B]">
                  {business.name || "İşletme"}
                </h1>

                {business.address ? (
                  <p className="mt-0.5 truncate text-xs leading-[17px] text-[#71717A]">
                    {business.address}
                  </p>
                ) : null}
              </div>

              <div className="flex min-h-8 shrink-0 items-center gap-1.5 rounded-full border border-[#E4E4E7] bg-white px-2.5">
                <span className="h-[7px] w-[7px] rounded-full bg-green-500" />
                <span className="text-[11px] font-bold text-[#52525B]">
                  Hazır
                </span>
              </div>
            </div>

            {menuUrl ? (
              <div className="rounded-[22px] border border-[#E4E4E7] bg-white p-4">
                <div className="mb-3.5">
                  <h2 className="text-lg font-extrabold text-[#18181B]">
                    Menü QR kodu
                  </h2>
                  <p className="mt-1 max-w-[310px] text-xs leading-[17px] text-[#71717A]">
                    Masada, tezgahta veya sosyal medyada kullanabilirsiniz.
                  </p>
                </div>

                <div className="flex justify-center">
                  <div className="rounded-[18px] border border-[#E4E4E7] bg-white p-3">
                    <QRCodeCanvas
                      id="business-qr-mobile"
                      value={menuUrl}
                      size={224}
                    />
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-center gap-2 px-2">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#EA580C]" />
                  <p className="text-center text-xs leading-[17px] text-[#71717A]">
                    Telefon kamerasıyla okutulduğunda menünüz açılır
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleOpenMenu}
                  className="mt-4 flex min-h-[58px] w-full items-center gap-3 rounded-[15px] border border-[#E4E4E7] bg-[#FAFAFA] px-3.5 text-left active:bg-[#F4F4F5]"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-[0.04em] text-[#A1A1AA]">
                      Menü adresi
                    </p>
                    <p className="mt-0.5 truncate text-[13px] font-semibold leading-[18px] text-[#52525B]">
                      {menuUrl}
                    </p>
                  </div>

                  <ExternalLink className="h-4 w-4 shrink-0 text-[#A1A1AA]" />
                </button>

                <button
                  type="button"
                  onClick={handleOpenMenu}
                  className="mt-3.5 flex min-h-[50px] w-full items-center justify-center gap-2 rounded-[15px] bg-[#EA580C] px-4 text-[15px] font-bold text-white active:opacity-70"
                >
                  <ExternalLink className="h-4 w-4" />
                  Menüyü Aç
                </button>

                <div className="mt-2.5 grid grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={handleShareQRImage}
                    className="flex min-h-12 items-center justify-center gap-2 rounded-[15px] border border-[#E4E4E7] bg-white px-2 text-sm font-bold text-[#3F3F46] active:bg-[#F4F4F5]"
                  >
                    <Share2 className="h-4 w-4" />
                    QR'ı Paylaş
                  </button>

                  <button
                    type="button"
                    onClick={handleShareMenu}
                    className="flex min-h-12 items-center justify-center gap-2 rounded-[15px] border border-[#E4E4E7] bg-white px-2 text-sm font-bold text-[#3F3F46] active:bg-[#F4F4F5]"
                  >
                    <Share2 className="h-4 w-4" />
                    Linki Paylaş
                  </button>
                </div>

                <div className="mt-2.5 grid grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={handleDownloadMobileQR}
                    className="flex min-h-11 items-center justify-center gap-2 rounded-[14px] px-2 text-xs font-semibold text-[#71717A] active:bg-[#F4F4F5]"
                  >
                    <Download className="h-4 w-4" />
                    QR'ı İndir
                  </button>

                  <button
                    type="button"
                    onClick={copyMenuUrl}
                    className="flex min-h-11 items-center justify-center gap-2 rounded-[14px] px-2 text-xs font-semibold text-[#71717A] active:bg-[#F4F4F5]"
                  >
                    <Copy className="h-4 w-4" />
                    Linki Kopyala
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-[22px] border border-[#E4E4E7] bg-white px-6 py-12 text-center">
                <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[#FFF7ED] text-xl font-extrabold text-[#EA580C]">
                  !
                </div>
                <h2 className="mt-3.5 text-[17px] font-bold text-[#18181B]">
                  QR kod oluşturulamıyor
                </h2>
                <p className="mt-1.5 text-[13px] text-[#71717A]">
                  İşletme menü bağlantısı bulunamadı.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* =========================================================
          DESKTOP
          Existing visual structure preserved.
         ========================================================= */}
      <div className="mx-auto hidden max-w-4xl p-4 sm:p-6 md:block lg:p-8">
        <h1 className="mb-2 text-3xl font-bold">QR Kod</h1>
        <p className="mb-8 text-zinc-600">
          Menünüz için QR kodu indirin.
        </p>

        {loading && <p>Yükleniyor...</p>}

        {!loading && !business && (
          <p className="text-red-500">
            Restoran bilgileri alınamadı.
          </p>
        )}

        {!loading && business && (
          <div className="space-y-6">
            <div className="rounded-xl border bg-white p-4">
              <p className="font-semibold">Restoran:</p>
              <p>{business.name}</p>
              <p className="break-words text-sm text-zinc-600">
                {business.address}
              </p>
            </div>

            <div className="flex flex-col items-center gap-4 overflow-hidden rounded-xl border bg-white p-4 sm:p-6">
              {menuUrl ? (
                <QRCodeCanvas
                  id="business-qr"
                  value={menuUrl}
                  size={256}
                  includeMargin
                />
              ) : (
                <p className="text-center text-sm text-red-500">
                  Restoran slug bilgisi bulunamadı. QR kod oluşturulamıyor.
                </p>
              )}

              <p className="max-w-full break-all text-center text-sm text-zinc-600">
                {menuUrl || "Link oluşturulamadı"}
              </p>

              <Button onClick={handleDownloadQR} disabled={!menuUrl}>
                QR Kodunu İndir
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
